import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import { Suggestion } from '../../_models/common/suggestion.model';
import { backgroundContext } from '../../_helpers/loading.interceptor';

/**
 * Suggestions for the staff search boxes: tutors and client referrers whose name contains q (trimmed). The API
 * returns at most 10, and nothing under 2 characters. Searches are behind the search.autocomplete switches, so a
 * 404 or any other failure is just no suggestions; the typeahead swallows the error.
 *
 * `tutors` and `referrers` are arrow properties so a template can pass them straight to `app-typeahead`'s source.
 * A GET with no body, so prepareOptions(null), which observes the full response; the body is unwrapped. A background
 * request, so typing does not show the spinner.
 */
@Injectable({ providedIn: 'root' })
export class SuggestService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient, protected hashManagerService: SessionUserHashManagerService) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  public readonly tutors = (q: string): Observable<Suggestion[]> => this.suggest('Tutors', q);

  public readonly referrers = (q: string): Observable<Suggestion[]> => this.suggest('Referrers', q);

  /** A suggestion's display text, for `displayWith`. */
  public readonly name = (s: Suggestion): string => s?.name ?? '';

  private suggest(kind: string, q: string): Observable<Suggestion[]> {
    const params = new HttpParams().set('q', (q ?? '').trim());
    return this.http.get(this.apiUrl + 'api/Suggest/' + kind, { ...this.prepareOptions(null), params, context: backgroundContext() }).pipe(
      map((data: any) => ((data ? data.body : data) ?? []) as Suggestion[]),
      catchError(this.handleError('suggest' + kind))
    );
  }
}
